/**
 * Why the Workflow2 redesign's Save button is off, what is wrong with a typed name, and the name a workflow
 * gets when nobody types one (2026-10-10; optional name 2026-10-11).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Plan: src/pages/Workflow2/PROJECT_NOTES.md (Workflow UI overhaul, step P1). The new builder shows the FIRST blocker as
 * a hint under the disabled Save button and nameError() under the name field.
 *
 * WHY A NAME HAS RULES AT ALL. Nothing runs by name: at run time a workflow is found by its site and trigger.
 * The name is stored, shown in the lists, and used ONCE — the first Activate creates the Step Functions state
 * machine `${siteID}-${Name}` (workflow-service enableWorkflow). State-machine names must be unique in the AWS
 * account, use letters / digits / dashes / underscores, and run to 80 characters at most. Hence the rules on a
 * name that IS typed, kept exactly as the classic builder's (WorkflowBuilder.vue `isValidWorkflowName` + the
 * checks in `saveWorkflow`), except the cap:
 *
 *   • letters, digits, dashes and underscores only (`/^[a-zA-Z0-9_-]+$/`) — so NO spaces, and the name is
 *     NOT trimmed before the check (a trailing space is an invalid character, exactly as the classic save
 *     rejects it);
 *   • at most 43 characters: the site id is 36, plus the dash, plus 43 = 80. Classic caps at 44, which lets a
 *     44-character name through to a first Activate that then fails at AWS; the redesign is one tighter on
 *     purpose (cypress/node-tests/workflow-save-blockers.test.js pins both numbers);
 *   • not the name of another workflow, compared case-insensitively (`otherNames` must already leave out
 *     the workflow being edited).
 *
 * THE NAME IS OPTIONAL in the redesigned builder (Nilay, 2026-10-11: the trigger is the workflow; the name
 * carries nothing). A blank name no longer blocks Save. When the box is blank the page sends the trigger's
 * label as a slug (`autoName`: "Appointment scheduled" → `appointment-scheduled`, `-2` / `-3` while that is
 * taken) so the row, the classic list and the state machine all get a valid, readable name, and the new pages
 * show the trigger's label instead of it (`isAutoName`). A typed name is never touched.
 *
 * Pure, and never throws on null / undefined / garbage inputs.
 */

// 80 (AWS Step Functions state-machine name) − 36 (site id) − 1 (the dash).
export const NAME_MAX = 43;

// KEEP IN SYNC with WorkflowBuilder.vue `isValidWorkflowName`.
export const NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

export function isValidName(name) {
  return typeof name === 'string' && NAME_PATTERN.test(name);
}

function isBlank(name) {
  return typeof name !== 'string' || name.trim() === '';
}

function cleanNames(otherNames) {
  return (Array.isArray(otherNames) ? otherNames : []).filter(n => typeof n === 'string').map(n => n.toLowerCase());
}

/** '' when the name is fine (or blank: a blank name is allowed), else the one-line problem to show under the field. */
export function nameError(name, otherNames) {
  if (isBlank(name)) return '';
  if (!isValidName(name)) return 'Use letters, numbers, dashes or underscores, with no spaces.';
  if (name.length > NAME_MAX) return 'Keep it to ' + NAME_MAX + ' characters. This one is ' + name.length + '.';
  const lower = name.toLowerCase();
  if (cleanNames(otherNames).indexOf(lower) !== -1) return 'Another workflow is already called ' + name + '.';
  return '';
}

/** '' when Save may proceed, else the first thing in the way (this order is the order the user should fix them in). */
export function saveBlocker(args) {
  const a = args !== null && typeof args === 'object' ? args : {};
  if (nameError(a.name, a.otherNames)) return 'Fix the name to save';
  if (!a.hasTrigger) return 'Add a trigger to save';
  if (!(Number(a.actionCount) > 0)) return 'Add an action to save';
  if (a.hasLongText) return 'Shorten the long text to save';
  return '';
}

/** "Appointment scheduled" → "appointment-scheduled": lower case, runs of anything else become one dash. '' for garbage. */
export function autoNameBase(triggerLabel) {
  if (typeof triggerLabel !== 'string') return '';
  return triggerLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, NAME_MAX);
}

/**
 * The name a workflow gets when the box is blank: the trigger's slug, or the slug with `-2`, `-3`, ... while a
 * workflow of that name exists (case-insensitive, like nameError). Always a valid name within NAME_MAX, or ''
 * when there is no label to build it from (then Save is already blocked by "Add a trigger").
 */
export function autoName(triggerLabel, otherNames) {
  const base = autoNameBase(triggerLabel);
  if (!base) return '';
  const taken = cleanNames(otherNames);
  if (taken.indexOf(base) === -1) return base;
  for (let n = 2; n < 1000; n++) {
    const suffix = '-' + n;
    const candidate = base.slice(0, NAME_MAX - suffix.length) + suffix;
    if (taken.indexOf(candidate) === -1) return candidate;
  }
  return '';
}

/** True when `name` is what autoName would make for this trigger (the slug, or the slug with a numeric suffix), ignoring case. */
export function isAutoName(name, triggerLabel) {
  const base = autoNameBase(triggerLabel);
  if (!base || typeof name !== 'string') return false;
  const lower = name.toLowerCase();
  if (lower === base) return true;
  const m = /^(.+)(-\d+)$/.exec(lower);
  if (!m) return false;
  // The suffixed form keeps as much of the slug as fits beside the suffix (see autoName).
  return m[1] === base.slice(0, NAME_MAX - m[2].length);
}
